<template>
  <div class="bg-putty relative pb-4">
    <div
      v-show="isFilterSectionVisible"
      :aria-expanded="isFilterSectionVisible"
      :aria-hidden="!isFilterSectionVisible"
      class="filters bg-charcoal abolute inset-x-0 top-0"
    >
      <div class="container">
        <div class="relative p-6 fluid-xs leading-tightest text-white">
          <!-- Clear refinements button -->
          <div v-if="hasRefinements" class="absolute top-0 right-0 mt-4 mr-4">
            <button @click.prevent="clearFilters" class="text-white text-sm underline outline-br-green!">
              {{ $t('exercises.resetFilters') }}
            </button>
          </div>

          <!-- Facet lists -->
          <div v-for="(facet, index) in facets" :key="facet" class="mb-6">
            <CustomFacetList :facet="facet" :initial-open="index === 0" />
          </div>
        </div>
      </div>
    </div>

    <!-- Results section -->
    <div class="container pt-12">
      <div class="flex flex-row justify-between">
        <button class="fluid-sm font-medium" @click="$emit('backClick')">
          <InlineArrowIcon class="inline transform rotate-180 h-2" />
          {{ $t('exercises.backToTitle', { title: returnToTitle }) }}
        </button>
        <div class="fluid-sm leading-tightest font-medium">{{ totalHits }} {{ $t(`exercises.${resultType}s`) }}</div>
      </div>

      <FilterListingResultTitle id="resultListTitle" :result-type="resultType" />

      <div class="mt-12">
        <ExerciseList v-if="resultType === 'exercise'" :exercises="results" />
        <RoutineList v-if="resultType === 'routine'" :routines="results" :tag-facets="facets.slice(0, 2)" />
      </div>

      <!-- Pagination -->
      <div v-if="Number(totalPages) > 1 || forceShowPagination" class="w-full my-6 flex justify-center">
        <div class="flex flex-wrap">
          <!-- Previous page button -->
          <button
            v-if="Number(currentPage) > 0"
            @click="changePage(Number(currentPage || 0) - 1)"
            class="mr-4 font-medium border rounded-full px-4 py-1 text-white bg-black"
            :aria-label="$t('navigation.previous-page')"
          >
            &laquo;
          </button>

          <!-- Page numbers -->
          <button
            v-for="page in displayedPages"
            :key="page"
            @click="changePage(page)"
            class="mr-4 font-medium border rounded-full px-4 py-1"
            :class="
              Number(currentPage || 0) === page
                ? 'border-br-green-light bg-br-green-light text-charcoal'
                : 'text-white border-white bg-black'
            "
            :aria-current="Number(currentPage || 0) === page ? 'page' : null"
          >
            {{ page + 1 }}
          </button>

          <!-- Next page button -->
          <button
            v-if="Number(currentPage || 0) < Number(totalPages || 1) - 1"
            @click="changePage(Number(currentPage || 0) + 1)"
            class="mr-4 font-medium border rounded-full px-4 py-1 text-white bg-black"
            aria-label="Next page"
          >
            &raquo;
          </button>
        </div>
      </div>

      <!-- Results count info -->
      <div v-if="Number(totalHits) > 0 || forceShowPagination" class="container text-center mb-6 text-sm text-gray-600">
        {{
          $t('exercises.showingResults', {
            from: Number(currentPage || 0) * hitsPerPage + 1,
            to: Math.min((Number(currentPage || 0) + 1) * hitsPerPage, Number(totalHits)),
            total: Number(totalHits),
          })
        }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { scrollToSelectorRespectingBars } from '@/utils/common';
import { useAlgoliaSearch } from '@/composables/useAlgoliaSearch';
import RoutineList from '@/components/exercises/RoutineList';
import ExerciseList from '@/components/exercises/ExerciseList';
import InlineArrowIcon from '@/components/icons/InlineArrowIcon';
import CustomFacetList from '@/components/exercises/CustomFacetList';
import FilterListingResultTitle from '@/components/exercises/FilterListingResultTitle';

export default defineNuxtComponent({
  components: {
    InlineArrowIcon,
    ExerciseList,
    RoutineList,
    CustomFacetList,
    FilterListingResultTitle,
  },

  props: {
    facets: {
      type: Array,
      required: true,
    },
    resultType: {
      type: String,
      required: true,
    },
    results: {
      type: Array,
      default: () => [],
    },
    indexName: {
      type: String,
      required: true,
    },
    isFilterSectionVisible: {
      type: Boolean,
      default: true,
    },
    hasRefinements: {
      type: Boolean,
      default: false,
    },
    hitsPerPage: {
      type: Number,
      default: 12,
    },
    currentPage: {
      type: Number,
      default: 0,
    },
    totalPages: {
      type: Number,
      default: 0,
    },
    totalHits: {
      type: Number,
      default: 0,
    },
    returnToTitle: {
      type: String,
      default: null,
    },
  },

  emits: ['backClick'],

  async setup(props) {
    const forceShowPagination = ref(false);

    const { changePage, clearFilters } = await useAlgoliaSearch();

    // Calculate displayed page numbers (show max 5 pages with current page in the middle when possible)
    const displayedPages = computed(() => {
      // If forcing pagination and no real pages, create fake pages for testing
      if (forceShowPagination && (!props.totalPages || Number(props.totalPages) <= 1)) {
        return [0, 1, 2]; // Show 3 test pages (0-indexed)
      }

      const total = Number(props.totalPages) || 0;
      if (total <= 0) {
        return [0]; // Always show at least page 1
      }

      if (total <= 5) {
        // Show all pages if 5 or fewer
        return Array.from({ length: total }, (_, i) => i);
      }

      // Calculate page range with current page in the middle
      const current = Number(props.currentPage) || 0;
      let start = Math.max(0, current - 2);
      let end = Math.min(total - 1, start + 4);

      // Adjust if we're near the end
      if (end === total - 1) {
        start = Math.max(0, end - 4);
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    });

    // Scroll to results title
    const scrollTop = () => {
      scrollToSelectorRespectingBars('#resultListTitle');
    };

    // Watch for page changes to scroll to top
    watch(
      () => props.currentPage,
      () => {
        scrollTop();
      }
    );

    return {
      scrollTop,
      displayedPages,
      forceShowPagination,
      clearFilters,
      changePage,
    };
  },
});
</script>
